import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../integrations/supabase/client";
import { toast } from "sonner";
import { Capacitor } from "@capacitor/core";
import { useTranslation } from "react-i18next";

const AuthCallback = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoading, session } = useAuth();

  useEffect(() => {
    const handleAuthCallback = async () => {
      console.log('🔗 AuthCallback component mounted');

      const hashParams = new URLSearchParams(location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');

      const urlParams = new URLSearchParams(location.search);
      const authCode = urlParams.get('code');

      if (accessToken && refreshToken) {
        console.log('🔐 Processing hash tokens');
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });

        if (sessionError) {
          console.error('Session error:', sessionError);
          toast.error(t('authCallback.sessionError'));
          if (Capacitor.isNativePlatform()) {
            navigate('/auth', { replace: true });
          }
          return;
        }
      } else if (authCode) {
        console.log('🔐 Processing PKCE auth code');
        const { error: sessionError } = await supabase.auth.exchangeCodeForSession(authCode);

        if (sessionError) {
          console.error('Session error:', sessionError);
          toast.error(t('authCallback.codeExchangeError'));
          if (Capacitor.isNativePlatform()) {
            navigate('/auth', { replace: true });
          }
          return;
        }
      }

      if (user && !isLoading) {
        console.log('✅ User authenticated, redirecting...');
        toast.success(t('authCallback.authSuccess'));
        navigate('/feed', { replace: true });
      } else if (!isLoading) {
        console.log('❌ User not authenticated, redirecting to login');
        navigate('/auth', { replace: true });
      }
    };
    
    if (!isLoading && !session) {
      handleAuthCallback();
    }
  }, [user, isLoading, navigate, location.hash, location.search, session, t]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <h2 className="text-xl font-semibold text-foreground mb-2">{t('authCallback.verifying')}</h2>
        <p className="text-muted-foreground">{t('authCallback.pleaseWait')}</p>
      </div>
    </div>
  );
};

export default AuthCallback;
